import { useState, useEffect } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  LayoutDashboard, Users, Upload, Shield, ScrollText, User, Database,
  LogOut, UserCircle, Newspaper, FolderOpen, FolderTree, Mail, ChevronDown, CalendarDays,
  MapPin, Network, ListTree, HandCoins, SlidersHorizontal,
} from "lucide-react";

const LOGO = "/logo-cfhtb-horizontal.png";

const memberLinks = [
  { to: "/", label: "Tableau de bord", icon: LayoutDashboard, end: true },
  { to: "/seminaires", label: "Événements", icon: CalendarDays },
  { to: "/actualites", label: "Actualités", icon: Newspaper },
  { to: "/annuaire", label: "Annuaire", icon: MapPin },
  { to: "/documents", label: "Documents", icon: FolderOpen },
  { to: "/contact", label: "Contact", icon: Mail },
];

const adminSections = [
  {
    titre: null,
    links: [
      { to: "/admin", label: "Tableau de bord", icon: LayoutDashboard, end: true },
      { to: "/admin/membres", label: "Membres", icon: Users, end: true },
      { to: "/admin/seminaires", label: "Événements", icon: CalendarDays, end: true },
      { to: "/admin/documents", label: "Documents", icon: FolderOpen, end: true },
      { to: "/admin/actualites", label: "Actualités", icon: Newspaper },
      { to: "/admin/annuaire", label: "Annuaire", icon: MapPin, end: true },
      { to: "/admin/messagerie", label: "Messagerie", icon: Mail, end: true },
      { to: "/admin/helloasso", label: "HelloAsso", icon: HandCoins, end: true },
    ],
  },
  {
    titre: "Administration",
    links: [
      { to: "/admin/utilisateurs", label: "Administrateurs", icon: Shield },
      { to: "/admin/journal", label: "Journal d'audit", icon: ScrollText },
      { to: "/admin/messagerie/parametres", label: "Messagerie", icon: Mail },
      { to: "/admin/referentiels", label: "Référentiel", icon: ListTree },
      { to: "/admin/modules", label: "Modules", icon: SlidersHorizontal },
      { to: "/admin/architecture", label: "Architecture", icon: Network },
    ],
  },
];

export function AppLayout({ variant = "membre", children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [adminMenuOpen, setAdminMenuOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [modules, setModules] = useState({ annuaire_visible: true });
  useEffect(() => { api("/modules").then(setModules).catch(() => {}); }, []);
  const mainLinks = variant === "admin"
    ? adminSections[0].links
    : memberLinks.filter((l) => l.to !== "/annuaire" || modules.annuaire_visible);
  const adminActive = ["/admin/utilisateurs", "/admin/journal", "/admin/messagerie/parametres", "/admin/tags", "/admin/architecture", "/admin/referentiels", "/admin/modules"].some(
    (p) => location.pathname.startsWith(p)
  );
  const displayName = user?.prenom || user?.nom
    ? `${user.prenom || ""} ${user.nom || ""}`.trim()
    : user?.email;
  const initials = ((user?.prenom?.[0] || "") + (user?.nom?.[0] || "")).toUpperCase() || (user?.email?.[0] || "?").toUpperCase();

  const handleLogout = async () => {
    await logout();
    toast.success("Vous êtes déconnecté");
    navigate("/connexion");
  };

  return (
    <div className="min-h-screen bg-cfhtb-bg">
      <header className="sticky top-0 z-50 bg-white border-b border-slate-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3 min-w-0">
            <img src={LOGO} alt="CFHTB — Confédération Francophone d'Hypnose et Thérapies Brèves" className="h-11 sm:h-12 w-auto" data-testid="app-logo" />
          </div>
          <div className="flex items-center gap-3">
            {user?.is_admin && (
              <button
                data-testid="switch-space-button"
                onClick={() => navigate(variant === "admin" ? "/" : "/admin")}
                className="hidden sm:inline-flex items-center gap-1.5 text-xs font-medium text-cfhtb-primary border border-cfhtb-primary rounded-md px-3 py-1.5 hover:bg-cfhtb-bg transition-colors"
              >
                <UserCircle className="h-3.5 w-3.5" />
                {variant === "admin" ? "Espace adhérent" : "Back-office"}
              </button>
            )}
            <div className="relative" onMouseLeave={() => setUserMenuOpen(false)}>
              <button
                data-testid="user-menu-button"
                onClick={() => setUserMenuOpen((o) => !o)}
                className="inline-flex items-center gap-2 rounded-md px-2.5 py-1.5 text-sm font-medium text-cfhtb-heading hover:bg-cfhtb-bg border border-transparent hover:border-cfhtb-light/40 transition-colors"
              >
                <span className="h-7 w-7 rounded-full bg-cfhtb-primary/10 text-cfhtb-primary flex items-center justify-center text-xs font-semibold shrink-0">
                  {initials}
                </span>
                <span className="hidden md:block truncate max-w-[200px]" data-testid="current-user-email">
                  {displayName}
                </span>
                <ChevronDown className={`h-3.5 w-3.5 text-cfhtb-text transition-transform ${userMenuOpen ? "rotate-180" : ""}`} />
              </button>
              {userMenuOpen && (
                <div
                  data-testid="user-menu"
                  className="absolute right-0 top-full z-50 mt-1 w-56 bg-white rounded-md border border-slate-200 shadow-lg py-1 animate-fade-in"
                >
                  <NavLink
                    to="/profil"
                    onClick={() => setUserMenuOpen(false)}
                    data-testid="nav-mon-profil"
                    className={({ isActive }) =>
                      `flex items-center gap-2 px-4 py-2.5 text-sm transition-colors ${
                        isActive ? "text-cfhtb-primary bg-cfhtb-bg font-medium" : "text-cfhtb-text hover:bg-cfhtb-bg/60 hover:text-cfhtb-heading"
                      }`
                    }
                  >
                    <User className="h-4 w-4" /> Mon profil
                  </NavLink>
                  <NavLink
                    to="/profil/donnees"
                    onClick={() => setUserMenuOpen(false)}
                    data-testid="nav-mes-donnees"
                    className={({ isActive }) =>
                      `flex items-center gap-2 px-4 py-2.5 text-sm transition-colors ${
                        isActive ? "text-cfhtb-primary bg-cfhtb-bg font-medium" : "text-cfhtb-text hover:bg-cfhtb-bg/60 hover:text-cfhtb-heading"
                      }`
                    }
                  >
                    <Database className="h-4 w-4" /> Mes données
                  </NavLink>
                  <div className="border-t border-slate-100 my-1" />
                  <button
                    data-testid="logout-button"
                    onClick={handleLogout}
                    className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-cfhtb-error hover:bg-cfhtb-error/5 transition-colors"
                  >
                    <LogOut className="h-4 w-4" /> Déconnexion
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
        <nav className="border-t border-slate-100 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center flex-wrap">
            {mainLinks.map(({ to, label, icon: Icon, end }) => (
              <NavLink
                key={to}
                to={to}
                end={end}
                data-testid={`nav-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                className={({ isActive }) =>
                  `inline-flex items-center gap-2 whitespace-nowrap px-3 py-2.5 text-sm font-medium border-b-2 transition-colors ${
                    isActive
                      ? "border-cfhtb-primary text-cfhtb-primary"
                      : "border-transparent text-cfhtb-text hover:text-cfhtb-heading hover:border-cfhtb-light"
                  }`
                }
              >
                <Icon className="h-4 w-4" />
                {label}
              </NavLink>
            ))}
            {variant === "admin" && (
              <div className="relative" onMouseLeave={() => setAdminMenuOpen(false)}>
                <button
                  data-testid="nav-administration"
                  onClick={() => setAdminMenuOpen((o) => !o)}
                  className={`inline-flex items-center gap-2 whitespace-nowrap px-3 py-2.5 text-sm font-medium border-b-2 transition-colors ${
                    adminActive
                      ? "border-cfhtb-primary text-cfhtb-primary"
                      : "border-transparent text-cfhtb-text hover:text-cfhtb-heading hover:border-cfhtb-light"
                  }`}
                >
                  <Shield className="h-4 w-4" />
                  Administration
                  <ChevronDown className={`h-3.5 w-3.5 transition-transform ${adminMenuOpen ? "rotate-180" : ""}`} />
                </button>
                {adminMenuOpen && (
                  <div
                    data-testid="administration-submenu"
                    className="absolute right-0 top-full z-50 w-56 bg-white rounded-md border border-slate-200 shadow-lg py-1 animate-fade-in"
                  >
                    {adminSections[1].links.map(({ to, label, icon: Icon }) => (
                      <NavLink
                        key={to}
                        to={to}
                        onClick={() => setAdminMenuOpen(false)}
                        data-testid={`nav-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                        className={({ isActive }) =>
                          `flex items-center gap-2 px-4 py-2.5 text-sm transition-colors ${
                            isActive
                              ? "text-cfhtb-primary bg-cfhtb-bg font-medium"
                              : "text-cfhtb-text hover:bg-cfhtb-bg/60 hover:text-cfhtb-heading"
                          }`
                        }
                      >
                        <Icon className="h-4 w-4" />
                        {label}
                      </NavLink>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </nav>
      </header>
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">{children}</main>
    </div>
  );
}
